import React, { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Clock,
  Linkedin,
  Mail,
  MessageCircle,
  Phone,
  Sparkles,
  Video,
} from "lucide-react";
import { useFetchInteractionTranscript } from "../../hooks/useAccountConversations";
import CommonAvatar from "@/shared/components/ui/Avatar/CommonAvatar";
import { parseTranscriptSegments } from "../../utils/formatTranscriptText";
import { Spinner } from "@heroui/react";

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function transcriptUnavailableMessage(error) {
  const status = error?.response?.status;
  const code = error?.response?.data?.code;

  if (status === 404 || code === "ONLINE_MEETING_NOT_FOUND") {
    return "Meeting not found.";
  }
  if (code === "MS_GRAPH_TRANSCRIPTS_DISABLED") {
    return "Meeting transcripts are disabled for your organization.";
  }
  return "Transcript unavailable.";
}

function resolveInteractionPresentation(interaction) {
  const sourceType = interaction?.source_type;
  const channel = interaction?.channel || sourceType;
  const direction = interaction?.meta?.direction;
  const isInbound = direction === "inbound";

  if (
    sourceType === "meeting" ||
    (channel === "microsoft" && sourceType === "meeting")
  ) {
    return {
      Icon: Video,
      label: "Meeting initiated",
      showTranscript: true,
    };
  }

  if (sourceType === "engagement") {
    return {
      Icon: Sparkles,
      label: interaction?.subject || "Campaign activity",
      showTranscript: false,
    };
  }

  if (sourceType === "voice" || channel === "voice") {
    return {
      Icon: Phone,
      label: interaction?.subject || "Call",
      showTranscript: false,
      showPreviewExpanded: true,
    };
  }

  if (sourceType === "linkedin" || channel === "linkedin") {
    return {
      Icon: Linkedin,
      label: isInbound ? "LinkedIn message received" : "LinkedIn message sent",
      showTranscript: false,
    };
  }

  if (sourceType === "whatsapp" || channel === "whatsapp") {
    return {
      Icon: MessageCircle,
      label: isInbound ? "WhatsApp message received" : "WhatsApp message sent",
      showTranscript: false,
    };
  }

  if (sourceType === "email" || channel === "email") {
    if (['direct_relationship', 'direct_relationship_reply'].includes(interaction?.meta?.provider)) {
      const status = interaction?.meta?.status;
      const label = isInbound ? 'Direct email received' : ({
        pending: 'Direct email awaiting confirmation', blocked: 'Direct email blocked', failed: 'Direct email failed',
      })[status] || 'Direct email sent';
      return { Icon: Mail, label, showTranscript: false };
    }
    const isMicrosoft = interaction?.meta?.provider === "microsoft";
    if (isMicrosoft) {
      return {
        Icon: Mail,
        label: isInbound ? "Email received" : "Email sent",
        showTranscript: false,
      };
    }
    return {
      Icon: Mail,
      label: isInbound ? "Campaign email received" : "Campaign email sent",
      showTranscript: false,
    };
  }

  return {
    Icon: Mail,
    label: interaction?.subject || "Interaction",
    showTranscript: false,
  };
}

function matchesParticipantToContact(participant, contact) {
  const participantEmail = participant?.email?.toLowerCase();
  const contactEmail = contact?.email?.toLowerCase();
  if (participantEmail && contactEmail && participantEmail === contactEmail) {
    return true;
  }

  const participantName = participant?.name?.toLowerCase();
  const contactName = contact?.contact_name?.toLowerCase();
  return Boolean(
    participantName && contactName && participantName === contactName,
  );
}

function orderParticipantsForContactFilter(
  participants,
  filterContactId,
  contacts,
) {
  if (!filterContactId) return participants;

  const selectedContact = contacts.find(
    (contact) => contact.id === filterContactId,
  );
  if (!selectedContact) return participants;

  const selectedIndex = participants.findIndex((participant) =>
    matchesParticipantToContact(participant, selectedContact),
  );
  if (selectedIndex <= 0) return participants;

  return [
    participants[selectedIndex],
    ...participants.filter((_, index) => index !== selectedIndex),
  ];
}

const ParticipantAvatars = ({
  participants = [],
  filterContactId = null,
  contacts = [],
  maxVisible = 2,
}) => {
  if (!participants.length) return null;

  const orderedParticipants = orderParticipantsForContactFilter(
    participants,
    filterContactId,
    contacts,
  );
  const visibleParticipants = orderedParticipants.slice(0, maxVisible);
  const remainingCount = Math.max(
    0,
    orderedParticipants.length - visibleParticipants.length,
  );

  return (
    <div className="flex items-center -space-x-1">
      {visibleParticipants.map((participant, index) => (
        <CommonAvatar
          key={`${participant.email || participant.name}-${index}`}
          name={participant.name || participant.email || "?"}
          size="sm"
        />
      ))}
      {remainingCount > 0 && (
        <div
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 text-[9px] font-semibold z-10 text-gray-600 dark:text-gray-300"
          aria-label={`${remainingCount} more participants`}
        >
          +{remainingCount}
        </div>
      )}
    </div>
  );
};

const TranscriptSegments = ({ text, clampLines = false, className = "" }) => {
  const segments = useMemo(
    () => parseTranscriptSegments(text?.trim() || ""),
    [text],
  );

  if (!segments.length) return null;

  const hasSpeakers = segments.some((segment) => segment.speaker);

  if (!hasSpeakers) {
    return (
      <p
        className={`text-[12px] leading-relaxed text-gray-600 dark:text-gray-300 ${
          clampLines ? "line-clamp-2" : ""
        } ${className}`}
      >
        {segments[0].content}
      </p>
    );
  }

  return (
    <div
      className={`flex flex-col gap-2.5 ${
        clampLines ? "line-clamp-2" : ""
      } ${className}`}
    >
      {segments.map((segment, index) => (
        <div
          key={`${segment.speaker || "line"}-${index}`}
          className="flex flex-col gap-0.5"
        >
          {segment.speaker && (
            <span className="text-[12px] font-semibold text-gray-900 dark:text-gray-100">
              {segment.speaker}
            </span>
          )}
          <span className="text-[12px] leading-relaxed text-gray-600 dark:text-gray-300">
            {segment.content}
          </span>
        </div>
      ))}
    </div>
  );
};

const InteractionCard = ({
  interaction,
  filterContactId = null,
  contacts = [],
}) => {
  const [expanded, setExpanded] = useState(false);
  const presentation = resolveInteractionPresentation(interaction);
  const { Icon, label, showTranscript, showPreviewExpanded } = presentation;
  const isMeeting = interaction?.source_type === "meeting";

  const {
    data: transcriptData,
    error: transcriptError,
    isError: isTranscriptError,
    isFetching: isTranscriptLoading,
  } = useFetchInteractionTranscript({
    interactionId: interaction?.id,
    enabled: expanded && isMeeting && !!interaction?.id,
  });

  const duration = interaction?.meta?.duration_minutes;
  const direction = interaction?.meta?.direction;
  const participants = interaction?.participants ?? [];

  return (
    <article className="flex gap-2 items-start w-full">
      <div className="flex items-center gap-2 mt-3">
        <ParticipantAvatars
          participants={participants}
          filterContactId={filterContactId}
          contacts={contacts}
          maxVisible={2}
        />
        <div className="shrink-0 text-right">
          <span className="text-[0.68rem] text-gray-500 font-normal leading-relaxed">
            {formatDate(interaction?.occurred_at)}
          </span>
        </div>
      </div>

      <div className="flex-1 min-w-0 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 relative">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 p-0.5"
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          {expanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>

        <div className="text-gray-500 leading-5 text-[11px] font-normal flex items-center gap-1 pr-6 flex-wrap">
          <Icon className="w-4 h-4 shrink-0" />
          <span>{label}</span>
          {direction && (
            <span
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-secondary-light-transparent text-secondary`}
            >
              {direction === "inbound" ? "Inbound" : "Outbound"}
            </span>
          )}
        </div>

        <h4 className="mt-1.5 text-[13px] font-semibold text-gray-900 dark:text-gray-100 leading-snug pr-6">
          {interaction?.subject || "Untitled"}
        </h4>

        {interaction?.meta?.provider === 'direct_relationship' && (
          <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-600 dark:text-gray-300">
            {[
              ['sent_at', 'Sent'], ['delivered_at', 'Delivered'], ['opened_at', 'Opened'],
              ['clicked_at', 'Link clicked'], ['replied_at', 'Human reply'],
              ['auto_reply_at', 'Automatic reply'], ['bounced_at', 'Bounced'],
            ].filter(([key]) => interaction.meta[key]).map(([key, text]) => (
              <span key={key} className="rounded bg-gray-100 dark:bg-gray-800 px-2 py-1"
                title={new Date(interaction.meta[key]).toLocaleString()}>
                {text} · {new Date(interaction.meta[key]).toLocaleString()}
              </span>
            ))}
            {!interaction.meta.sent_at && <span>{interaction.meta.status === 'pending'
              ? 'Send awaiting confirmation' : interaction.meta.status}</span>}
            {interaction.meta.suspected_automated_at && <span>Automated activity detected · excluded from engagement</span>}
            {interaction.meta.error_message && <span className="text-red-600">{interaction.meta.error_message}</span>}
          </div>
        )}
        {interaction?.meta?.provider === 'direct_relationship_reply' && (
          <p className="mt-1 text-[11px] text-gray-500">{({ human_reply: 'Human reply', auto_reply: 'Automatic reply',
            delivery_failure: 'Delivery failure', unknown: 'Awaiting verification' })[interaction.meta.reply_type]}
            {interaction.meta.reply_type === 'human_reply' && ` · ${interaction.meta.reply_intent}`}</p>
        )}

        {interaction?.preview?.trim() && (
          <div className="mt-1 pr-6">
            <TranscriptSegments
              text={interaction.preview}
              clampLines={!expanded && !showPreviewExpanded}
            />
          </div>
        )}

        <div className="mt-2 flex justify-between items-center gap-2 pr-1">
          {isMeeting && duration ? (
            <div className="flex items-center gap-1 text-[11px] text-gray-500">
              <Clock className="w-3.5 h-3.5 text-gray-500" />
              <span>{duration} min</span>
            </div>
          ) : (
            <span />
          )}
          <ParticipantAvatars
            participants={participants}
            filterContactId={filterContactId}
            contacts={contacts}
            maxVisible={5}
          />
        </div>

        {expanded && showTranscript && isMeeting && (
          <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-800 text-[12px] text-gray-600 dark:text-gray-300 leading-relaxed space-y-2">
            {isTranscriptLoading && (
              <div className="flex items-center justify-center h-full w-full">
                <Spinner size="sm" color="secondary" className="animate-spin" />
                <p className="text-gray-400 text-[12px] py-4 text-center">
                  Loading transcript...
                </p>
              </div>
            )}
            {!isTranscriptLoading && transcriptData?.data?.summary && (
              <TranscriptSegments
                text={transcriptData.data.summary}
                className={
                  transcriptData?.data?.transcript_unavailable
                    ? "text-amber-700"
                    : ""
                }
              />
            )}
            {!isTranscriptLoading &&
              isTranscriptError && (
                <span className="text-gray-400">
                  {transcriptUnavailableMessage(transcriptError)}
                </span>
              )}
            {!isTranscriptLoading &&
              !isTranscriptError &&
              transcriptData?.data?.content &&
              !transcriptData?.data?.transcript_unavailable && (
                <TranscriptSegments text={transcriptData.data.content} />
              )}
            {!isTranscriptLoading &&
              !isTranscriptError &&
              !transcriptData?.data?.summary && (
                <span className="text-gray-400">No transcript available.</span>
              )}
          </div>
        )}
      </div>
    </article>
  );
};

export default InteractionCard;
